/**
 * Optimized Product Rating Fetcher
 * - Batching: Collects ASINs and fetches in groups
 * - Intersection Observer: Only fetches when in/near viewport
 * - Caching: Avoids duplicate requests
 * - Mutation Observer: Handles dynamic slider clones & re-renders
 */
(function() {
  window.novRatingCache = window.novRatingCache || {};
  const RATING_CACHE = window.novRatingCache;
  const PENDING_ASINS = new Set();
  const FETCHING_ASINS = new Set();
  let fetchTimeout = null;

  /**
   * Batch fetch ASIN ratings from API
   */
  function scheduleBatchFetch() {
    clearTimeout(fetchTimeout);
    fetchTimeout = setTimeout(() => {
      if (PENDING_ASINS.size === 0) return;

      const asinsToFetch = Array.from(PENDING_ASINS).filter(asin => {
        return !FETCHING_ASINS.has(asin) && !RATING_CACHE[asin];
      });

      if (asinsToFetch.length === 0) {
        PENDING_ASINS.clear();
        return;
      }

      asinsToFetch.forEach(asin => FETCHING_ASINS.add(asin));
      PENDING_ASINS.clear();

      const apiUrl = `https://phplaravel-1456016-5692654.cloudwaysapps.com/api/rest/v1/get-rating-summary?asins=${asinsToFetch.join(',')}`;

      fetch(apiUrl)
        .then(res => res.json())
        .then(result => {
          const payload = result.ratings || result.data || result;
          
          // Mark all requested ASINs as attempted/cached
          asinsToFetch.forEach(asin => {
            const normalizedKey = asin.trim().toUpperCase();
            if (payload && payload[asin]) {
               RATING_CACHE[normalizedKey] = payload[asin];
            } else {
               // If missing from API, cache as empty to stop re-fetching
               RATING_CACHE[normalizedKey] = { total_reviews: 0, avg_rating: 0 };
            }
          });

          renderRatings();
        })
        .catch(err => console.error("Rating API Error:", err))
        .finally(() => {
          asinsToFetch.forEach(asin => FETCHING_ASINS.delete(asin));
        });
    }, 500);
  }

  /**
   * Generate SVG Star string
   */
  function generateStars(avg) {
    let stars = '<div style="display: inline-flex; gap: 2px; align-items: center; vertical-align: middle;">';
    const starSvg = `
      <svg width="12" height="12" viewBox="0 0 24 24" fill="#FFB400" xmlns="http://www.w3.org/2000/svg" style="display: block;">
        <path d="M12 1.7L15 8.3L22.3 9.4L17 14.5L18.3 21.7L12 18.3L5.7 21.7L7 14.5L1.7 9.4L9 8.3L12 1.7Z" />
      </svg>`;
    stars += starSvg;
    stars += '</div>';
    return stars;
  }

  /**
   * Render ratings for all visible boxes
   */
  function renderRatings() {
    const boxes = document.querySelectorAll(".rating-box:not([data-loaded])");
    if (boxes.length === 0) return;

    boxes.forEach(el => {
      const asin = (el.dataset.asin || "").trim().toUpperCase();
      if (!asin) return;

      if (RATING_CACHE[asin]) {
        const r = RATING_CACHE[asin];
        const avg = parseFloat(r.avg_rating || 0);
        const total = parseInt(r.total_reviews || 0);

        if (total > 0) {
          const starsEl = el.querySelector(".rating-stars");
          if (starsEl) {
            // Add the average value before stars
            starsEl.innerHTML = `<span style="margin-right: 4px; font-weight: 700; font-size: 12px; vertical-align: middle;">(${avg.toFixed(1)})</span>` + generateStars(avg);
          }

          const countEl = el.querySelector(".rating-count");
          if (countEl) countEl.innerHTML = `<span style="margin-left: 2px; font-size: 12px; vertical-align: middle;">(${total})</span>`;

          el.style.display = "flex";
          el.style.alignItems = "center";
          el.style.flexWrap = "nowrap";
          el.style.whiteSpace = "nowrap";
        } else {
          el.style.display = "none";
        }
        el.dataset.loaded = "true";
      }
    });
  }

  // Intersection Observer for performance
  const intersectionObserver = new IntersectionObserver((entries) => {
    let hasNew = false;
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const asin = entry.target.dataset.asin;
        if (asin) {
          const normAsin = asin.trim().toUpperCase();
          if (!RATING_CACHE[normAsin] && !PENDING_ASINS.has(normAsin)) {
            PENDING_ASINS.add(normAsin);
            hasNew = true;
          } else if (RATING_CACHE[normAsin]) {
            renderRatings();
          }
        }
        intersectionObserver.unobserve(entry.target);
      }
    });
    if (hasNew) scheduleBatchFetch();
  }, { rootMargin: '200px' });

  function attachObserver() {
    document.querySelectorAll(".rating-box:not([data-loaded])").forEach(el => {
      intersectionObserver.observe(el);
    });
  }

  // Initial Execution
  if (document.readyState === 'loading') {
    document.addEventListener("DOMContentLoaded", () => {
      attachObserver();
      renderRatings();
    });
  } else {
    attachObserver();
    renderRatings();
  }

  // Fallback for sliders/AJAX content - throttled
  let mutationTimeout;
  const mutationObserver = new MutationObserver((mutations) => {
    // Only trigger if nodes were added
    const hasAddedNodes = mutations.some(m => m.addedNodes.length > 0);
    if (!hasAddedNodes) return;

    clearTimeout(mutationTimeout);
    mutationTimeout = setTimeout(() => {
      attachObserver();
      renderRatings();
    }, 500);
  });
  mutationObserver.observe(document.body, { childList: true, subtree: true });

  // Handle click to scroll on product page
  document.addEventListener('click', (e) => {
    const clickableBox = e.target.closest('.rating-box');
    if (clickableBox) {
      const reviewSection = document.getElementById('product-reviews-section') || document.getElementById('AirReviews-BlockWrapper');
      if (reviewSection) {
        const offset = 100; // Offset for sticky header
        const bodyRect = document.body.getBoundingClientRect().top;
        const elementRect = reviewSection.getBoundingClientRect().top;
        const elementPosition = elementRect - bodyRect;
        const offsetPosition = elementPosition - offset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
  });

})();
